import {AccessibilityInfo, Alert, Pressable, View} from 'react-native';
import {IconButton} from 'react-native-paper';
import {Field} from '../../../Utils/SimpleForm';
import {RichText, Text} from '../../../Components';
import {useTheme} from '../../../Hooks';
import getStyles from './Question.styles';
import {QuestionId, getLocalizedQuestion} from '../../../Models/questionsI18n';
import {fillPlaceholders} from '../../../Models/fills';
import type {Answer} from '../../../Engine/boolean';
import type {AppLanguageCode} from '../../../Utils/I18n';

export interface QuestionProps {
  id: QuestionId;
  language?: AppLanguageCode;
  // Active module + answers so far, used to fill `[TRAUMA EVENT]` /
  // `[SYMPTOMS ENDORSED]` placeholders with what the respondent endorsed.
  moduleTarget?: string;
  observed?: ReadonlyMap<string, Answer>;
}

const Question: React.FC<QuestionProps> = (props) => {
  const {id, language = 'en', moduleTarget, observed} = props;
  const theme = useTheme();
  const styles = getStyles(theme);

  const base = getLocalizedQuestion(id, language);
  const question = {
    ...base,
    title: fillPlaceholders(base.title, moduleTarget, observed, language),
    description: fillPlaceholders(base.description ?? '', moduleTarget, observed, language),
  };
  const onReadAloudPress = () => {
    AccessibilityInfo.announceForAccessibility(
      [question.title, question.description].filter(Boolean).join('. '),
    );
  };
  const onHelpPress = () => {
    Alert.alert(
      'Answering this question',
      'Answer in the way that feels closest. You can choose Prefer not to answer.',
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.titleRow}>
        <Text
          weight="bold"
          fontSize={25}
          lineHeight={31}
          accessibilityRole="header"
          style={styles.title}
        >
          {question.title}
        </Text>
        <View style={styles.titleActions}>
          <IconButton
            icon="volume-high"
            mode="outlined"
            size={22}
            accessibilityLabel="Read question aloud"
            onPress={onReadAloudPress}
            style={styles.titleIconButton}
            iconColor={theme.colors.primary}
          />
          <IconButton
            icon="help-circle-outline"
            mode="outlined"
            size={22}
            accessibilityLabel="Help with answering this question"
            onPress={onHelpPress}
            style={styles.titleIconButton}
            iconColor={theme.colors.primary}
          />
        </View>
      </View>
      <RichText
        weight="medium"
        fontSize={16}
        lineHeight={24}
        style={styles.subtitle}
        text={question.description}
      />

      <Field id={id}>
        {({value: currentValue, changeValue}) => (
          <View style={styles.options}>
            {(question.sort
              ? question.sort.map((sortValue) => [sortValue, question.choices[sortValue]])
              : Object.entries(question.choices)
            ).map(([value, label]) => {
              const isSelected = currentValue === value;

              return (
                <View key={value} style={styles.optionRowWrap}>
                  <Pressable
                    accessibilityRole="radio"
                    accessibilityState={{checked: isSelected}}
                    accessibilityLabel={label}
                    accessibilityHint="Selects this answer for the current question."
                    onPress={() => changeValue(value)}
                    style={[styles.optionRow, isSelected && styles.optionRowSelected]}
                  >
                    <View
                      style={[styles.optionIndicator, isSelected && styles.optionIndicatorSelected]}
                    >
                      {isSelected ? <View style={styles.optionIndicatorDot} /> : null}
                    </View>

                    <Text
                      fontSize={17}
                      lineHeight={24}
                      weight={isSelected ? 'semiBold' : 'medium'}
                      style={[styles.optionLabel, isSelected && styles.optionLabelSelected]}
                    >
                      {label}
                    </Text>
                  </Pressable>
                </View>
              );
            })}
          </View>
        )}
      </Field>
    </View>
  );
};

export default Question;
